import React, { memo, useCallback } from 'react';
import { useDispatch } from 'react-redux';

import { Button } from '@mui/material';
import ErrorHandlingLayout from '@sharedCR/common/ErrorHandlingLayout';
import { ButtonGroupWrapper } from '@sharedCR/coreDesignSystem/Buttons';
import PropTypes from 'prop-types';

import { HORIZONTAL_ALIGNMENT } from 'constants/ui';

import { closeDialogAction } from 'store/global/actions';

import { LAYOUT_CLASS_NAME_MAP, ModalFormLayout } from './ModalFormWrapper.constants';

const ModalFormWrapper = ({
  children,
  layout = ModalFormLayout.MODAL,
  formRef = null,
  formTestId = '',
  submitButtonName = 'Save',
  cancelButtonName = 'Cancel',
  submitButtonTestId = 'submit-button',
  dataTestId = 'cancel-button',
  isWithCustomButtons = false,
  isFormChanged = false,
  isSubmitButtonDisabled = false,
  isSubmitting = false,
  isError = false,
  fetchData = null,
  onSubmit = null,
  onClose = null,
  onKeyDown = null,
}) => {
  const dispatch = useDispatch();

  const onCancel = useCallback(
    () => (onClose ? onClose() : dispatch(closeDialogAction())),
    [dispatch, onClose],
  );

  const onFormSubmit = useCallback(
    event => {
      event.preventDefault();
      onSubmit(event);
    },
    [onSubmit],
  );

  const form = (
    <form
      noValidate
      className={LAYOUT_CLASS_NAME_MAP[layout]}
      data-ischanged={isFormChanged}
      data-testid={formTestId}
      ref={formRef}
      onKeyDown={onKeyDown}
      onSubmit={onSubmit ? onFormSubmit : null}
    >
      {children}

      {!isWithCustomButtons && (
        <ButtonGroupWrapper alignment={HORIZONTAL_ALIGNMENT.RIGHT}>
          <Button
            data-testid={dataTestId}
            disabled={isSubmitting}
            size="large"
            type="button"
            variant="line"
            onClick={onCancel}
          >
            {cancelButtonName}
          </Button>

          {!!onSubmit && (
            <Button
              data-testid={submitButtonTestId}
              disabled={!isFormChanged || isSubmitting || isSubmitButtonDisabled}
              size="large"
              type="submit"
            >
              {submitButtonName}
            </Button>
          )}
        </ButtonGroupWrapper>
      )}
    </form>
  );

  return fetchData ? (
    <ErrorHandlingLayout isError={isError} onClick={fetchData}>
      {form}
    </ErrorHandlingLayout>
  ) : (
    form
  );
};

ModalFormWrapper.propTypes = {
  children: PropTypes.oneOfType([PropTypes.array, PropTypes.element]).isRequired,
  formRef: PropTypes.shape({ current: PropTypes.object }),
  cancelButtonName: PropTypes.string,
  dataTestId: PropTypes.string,
  fetchData: PropTypes.func,
  formTestId: PropTypes.string,
  isError: PropTypes.bool,
  isFormChanged: PropTypes.bool,
  isSubmitButtonDisabled: PropTypes.bool,
  isSubmitting: PropTypes.bool,
  isWithCustomButtons: PropTypes.bool,
  layout: PropTypes.oneOf(Object.values(ModalFormLayout)),
  submitButtonName: PropTypes.string,
  submitButtonTestId: PropTypes.string,
  onClose: PropTypes.func,
  onKeyDown: PropTypes.func,
  onSubmit: PropTypes.func,
};

export default memo(ModalFormWrapper);
